// ============================================================================
// quotaService — workspace performance tracking
// ----------------------------------------------------------------------------
// One active quota per user per workspace at a time. Progress is denormalized
// onto the quota doc; the workspace_activity ledger is the source-of-truth
// backup. Increments happen inside the same runTransaction that claims the
// taskCompletionLock — if the quota write fails, the task completion rolls
// back.
//
// Coaching: when a user passes "50% time elapsed" without reaching "50%
// attainment", QuotaService posts an AI Butler nudge to the #performance
// channel (created on demand; DM fallback). One-shot per period — guarded
// by the `coaching` field on the quota doc.
// ============================================================================

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
  type DocumentData,
  type Firestore,
  type Transaction,
} from 'firebase/firestore'

import { firestoreDb } from './firebase'
import { logAuditEvent } from './auditLedgerService'
import { subscribeInternal } from './automationOrchestrator'
import type { AutomationActor } from './automationService'
import type { AutomationTriggerEvent } from '../types/automation'
import { firestoreTimeToMillis } from '../utils/firestoreTime'
import {
  DEFAULT_QUOTA_COACHING_TEMPLATES,
  DEFAULT_QUOTA_COACHING_THRESHOLDS,
  isQuotaMet,
  PERFORMANCE_CHANNEL_SLUG,
  QUOTA_COLLECTION,
  QUOTA_TARGET_TYPES,
  quotaAttainment,
  type Quota,
  type QuotaAuditAction,
  type QuotaCoachingTrigger,
  type QuotaMemberContribution,
  type QuotaTargetMap,
  type QuotaTargetType,
} from '../types/quota'

const AI_BUTLER_UID = 'ai-butler'
const AI_BUTLER_NAME = 'AI Butler'

// ---------------------------------------------------------------------------
// Refs
// ---------------------------------------------------------------------------

const quotasCollection = (db: Firestore) => collection(db, QUOTA_COLLECTION)
const quotaDocRef = (db: Firestore, quotaId: string) => doc(db, QUOTA_COLLECTION, quotaId)

const audit = (
  db: Firestore,
  txn: Transaction,
  workspace: DocumentData | undefined,
  args: {
    workspaceId: string
    actorId: string
    actorName?: string | null
    action: QuotaAuditAction
    quotaId: string
    assigneeId: string
    summary: string
    payload?: Record<string, unknown>
  },
) => {
  logAuditEvent(db, txn, workspace, {
    workspaceId: args.workspaceId,
    actorId: args.actorId,
    actorName: args.actorName ?? null,
    action: args.action,
    activityType: 'QUOTA',
    targetId: args.quotaId,
    nodeType: 'QUOTA',
    targetTitle: `Quota · ${args.assigneeId}`,
    summary: args.summary,
    payload: args.payload,
  })
}

const toQuota = (docId: string, data: DocumentData): Quota => ({
  id: docId,
  workspaceId: data.workspaceId,
  assigneeId: data.assigneeId,
  targets: data.targets ?? {},
  startDate: data.startDate,
  endDate: data.endDate,
  status: data.status ?? 'ACTIVE',
  createdAt: data.createdAt,
  createdBy: data.createdBy,
  updatedAt: data.updatedAt,
  updatedBy: data.updatedBy,
  celebratedAt: data.celebratedAt ?? null,
  coaching: data.coaching ?? null,
  deletedAt: data.deletedAt ?? null,
  templateId: data.templateId ?? null,
  cadence: data.cadence ?? 'once',
  teamMemberIds: Array.isArray(data.teamMemberIds) ? data.teamMemberIds : [],
  customTargets: data.customTargets ?? {},
  rewardTiers: Array.isArray(data.rewardTiers) ? data.rewardTiers : [],
  notes: data.notes ?? '',
  pausedAt: data.pausedAt ?? null,
})


// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export interface CreateQuotaInput {
  workspaceId: string
  assigneeId: string
  targets: QuotaTargetMap
  startDate: Date
  endDate: Date
  actorId: string
  actorName?: string | null
  templateId?: string | null
  cadence?: 'once' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly'
  teamMemberIds?: string[]
  customTargets?: Record<string, { label: string; targetValue: number; currentValue: number; unit?: string }>
  rewardTiers?: Array<{ percent: number; label: string; badgeUrl?: string | null }>
  notes?: string
}

export const createQuota = async (input: CreateQuotaInput, db: Firestore = firestoreDb): Promise<string> => {
  if (input.startDate >= input.endDate) throw new Error('Quota startDate must precede endDate')
  if (Object.keys(input.targets).length === 0 && Object.keys(input.customTargets ?? {}).length === 0) {
    throw new Error('Quota must define at least one target')
  }

  const normalizedTargets: QuotaTargetMap = {}
  for (const [key, entry] of Object.entries(input.targets)) {
    if (!entry) continue
    normalizedTargets[key as QuotaTargetType] = {
      targetValue: Math.max(0, Math.floor(entry.targetValue)),
      currentValue: 0,
    }
  }

  const normalizedCustomTargets: Record<string, { label: string; targetValue: number; currentValue: number; unit?: string }> = {}
  for (const [key, value] of Object.entries(input.customTargets ?? {})) {
    if (!value || value.targetValue <= 0) continue
    normalizedCustomTargets[key] = {
      label: value.label,
      targetValue: value.targetValue,
      currentValue: 0,
      unit: value.unit,
    }
  }
  
  const sortedTiers = [...(input.rewardTiers ?? [])].sort((a, b) => a.percent - b.percent)

  const workspaceRef = doc(db, 'workspaces', input.workspaceId)
  const newRef = doc(quotasCollection(db))

  await runTransaction(db, async (txn) => {
    const workspaceSnap = await txn.get(workspaceRef)
    const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined

    // Enforce "one active quota per user per workspace" — read any existing
    // active doc inside the same transaction.
    const activeQuery = query(
      quotasCollection(db),
      where('workspaceId', '==', input.workspaceId),
      where('assigneeId', '==', input.assigneeId),
      where('status', '==', 'ACTIVE'),
    )
    const existing = await getDocs(activeQuery)
    const stillActive = existing.docs.filter((d) => !d.data().deletedAt)
    if (stillActive.length > 0) {
      throw new Error('User already has an active quota in this workspace')
    }

    txn.set(newRef, {
      workspaceId: input.workspaceId,
      assigneeId: input.assigneeId,
      targets: normalizedTargets,
      startDate: Timestamp.fromDate(input.startDate),
      endDate: Timestamp.fromDate(input.endDate),
      status: 'ACTIVE',
      createdAt: serverTimestamp(),
      createdBy: input.actorId,
      updatedAt: serverTimestamp(),
      updatedBy: input.actorId,
      celebratedAt: null,
      coaching: null,
      deletedAt: null,
      templateId: input.templateId ?? null,
      cadence: input.cadence ?? 'once',
      teamMemberIds: input.teamMemberIds ?? [],
      customTargets: normalizedCustomTargets,
      rewardTiers: sortedTiers,
      notes: input.notes ?? '',
      pausedAt: null,
    })

    audit(db, txn, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.actorId,
      actorName: input.actorName,
      action: 'quota.set',
      quotaId: newRef.id,
      assigneeId: input.assigneeId,
      summary: `Quota assigned`,
      payload: { targets: normalizedTargets, startDate: input.startDate.toISOString(), endDate: input.endDate.toISOString() },
    })
  })

  return newRef.id
}

export interface UpdateQuotaInput {
  workspaceId: string
  quotaId: string
  patch: Partial<{
    targets: QuotaTargetMap
    startDate: Date
    endDate: Date
    cadence: 'once' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly'
    teamMemberIds: string[]
    customTargets: Record<string, { label: string; targetValue: number; currentValue: number; unit?: string }>
    rewardTiers: Array<{ percent: number; label: string; badgeUrl?: string | null }>
    notes: string
    pausedAt: Timestamp | null
  }>
  actorId: string
  actorName?: string | null
}

export const updateQuota = async (input: UpdateQuotaInput, db: Firestore = firestoreDb): Promise<void> => {
  const ref = quotaDocRef(db, input.quotaId)
  const workspaceRef = doc(db, 'workspaces', input.workspaceId)

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref)
    if (!snap.exists()) throw new Error('Quota not found')
    const current = toQuota(snap.id, snap.data())
    const workspaceSnap = await txn.get(workspaceRef)
    const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined

    const next: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
      updatedBy: input.actorId,
    }
    
    let nextTargets = current.targets
    let nextCustomTargets = current.customTargets
    
    if (input.patch.targets) {
      const recomputed: QuotaTargetMap = {}
      for (const [key, entry] of Object.entries(input.patch.targets)) {
        if (!entry) continue
        const prior = current.targets[key as QuotaTargetType]
        recomputed[key as QuotaTargetType] = {
          targetValue: Math.max(0, Math.floor(entry.targetValue)),
          currentValue: prior ? prior.currentValue : 0,
        }
      }
      next.targets = recomputed
      nextTargets = recomputed
    }
    
    if (input.patch.customTargets) {
      const recomputedCustom: NonNullable<Quota['customTargets']> = {}
      for (const [key, entry] of Object.entries(input.patch.customTargets)) {
        if (!entry) continue
        const prior = current.customTargets?.[key]
        recomputedCustom[key] = {
          label: entry.label,
          targetValue: Math.max(0, Math.floor(entry.targetValue)),
          currentValue: prior ? prior.currentValue : 0,
          unit: entry.unit,
        }
      }
      next.customTargets = recomputedCustom
      nextCustomTargets = recomputedCustom
    }

    if (input.patch.targets || input.patch.customTargets) {
      if (!isQuotaMet({ targets: nextTargets, customTargets: nextCustomTargets })) {
        next.celebratedAt = null
      }
    }

    if (input.patch.startDate) next.startDate = Timestamp.fromDate(input.patch.startDate)
    if (input.patch.endDate) next.endDate = Timestamp.fromDate(input.patch.endDate)
    if (input.patch.cadence !== undefined) next.cadence = input.patch.cadence
    if (input.patch.teamMemberIds !== undefined) next.teamMemberIds = input.patch.teamMemberIds
    if (input.patch.notes !== undefined) next.notes = input.patch.notes
    if (input.patch.pausedAt !== undefined) next.pausedAt = input.patch.pausedAt
    if (input.patch.rewardTiers !== undefined) {
      next.rewardTiers = [...input.patch.rewardTiers].sort((a, b) => a.percent - b.percent)
    }

    txn.update(ref, next)

    audit(db, txn, workspace, {
      workspaceId: input.workspaceId,
      actorId: input.actorId,
      actorName: input.actorName,
      action: 'quota.update',
      quotaId: input.quotaId,
      assigneeId: current.assigneeId,
      summary: `Quota updated`,
      payload: { patch: { ...input.patch, targets: nextTargets } },
    })
  })
}

export const softDeleteQuota = async (
  args: { workspaceId: string; quotaId: string; actorId: string; actorName?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  const ref = quotaDocRef(db, args.quotaId)
  const workspaceRef = doc(db, 'workspaces', args.workspaceId)
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref)
    if (!snap.exists()) return
    const current = snap.data() as Quota
    const workspaceSnap = await txn.get(workspaceRef)
    const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined

    txn.update(ref, {
      deletedAt: serverTimestamp(),
      status: 'ARCHIVED',
      updatedAt: serverTimestamp(),
      updatedBy: args.actorId,
    })
    audit(db, txn, workspace, {
      workspaceId: args.workspaceId,
      actorId: args.actorId,
      actorName: args.actorName,
      action: 'quota.deleted',
      quotaId: args.quotaId,
      assigneeId: current.assigneeId,
      summary: 'Quota soft-deleted',
    })
  })
}

export const pauseQuota = async (
  input: {
    workspaceId: string
    quotaId: string
    actor: { id: string; name?: string | null }
  },
  db: Firestore = firestoreDb,
): Promise<void> => {
  const { workspaceId, quotaId, actor } = input
  const ref = quotaDocRef(db, quotaId)
  const wsRef = doc(db, 'workspaces', workspaceId)
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref)
    if (!snap.exists()) throw new Error('Quota not found')
    const quota = snap.data() as Quota
    const wsSnap = await txn.get(wsRef)
    const ws = wsSnap.exists() ? wsSnap.data() : undefined

    txn.update(ref, {
      pausedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actor.id,
    })

    audit(db, txn, ws, {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      action: 'quota.paused',
      quotaId,
      assigneeId: quota.assigneeId,
      summary: 'Paused quota',
    })
  })
}

export const resumeQuota = async (
  input: {
    workspaceId: string
    quotaId: string
    actor: { id: string; name?: string | null }
    extendOnResumeOverride?: boolean
    now?: Date | number
  },
  db: Firestore = firestoreDb,
): Promise<{ extendedDurationMs?: number }> => {
  const { workspaceId, quotaId, actor } = input
  const ref = quotaDocRef(db, quotaId)
  const wsRef = doc(db, 'workspaces', workspaceId)
  let extendedDurationMs: number | undefined

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref)
    if (!snap.exists()) throw new Error('Quota not found')
    const quota = snap.data() as Quota
    const wsSnap = await txn.get(wsRef)
    const ws = wsSnap.exists() ? wsSnap.data() : undefined

    const shouldExtend = input.extendOnResumeOverride ?? Boolean(ws?.settings?.quotaExtendOnResume)
    const nowMs = typeof input.now === 'number' ? input.now : (input.now ? input.now.getTime() : Date.now())

    const update: Record<string, unknown> = {
      pausedAt: null,
      updatedAt: serverTimestamp(),
      updatedBy: actor.id,
    }

    if (shouldExtend && quota.pausedAt) {
      const pausedAtMs = firestoreTimeToMillis(quota.pausedAt)
      const pauseDuration = Math.max(0, nowMs - pausedAtMs)
      extendedDurationMs = pauseDuration
      if (quota.endDate) {
        const currentEndMs = firestoreTimeToMillis(quota.endDate)
        const newEndMs = currentEndMs + pauseDuration
        update.endDate = Timestamp.fromMillis(newEndMs)
      }
    }

    txn.update(ref, update)

    audit(db, txn, ws, {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      action: 'quota.resumed',
      quotaId,
      assigneeId: quota.assigneeId,
      summary: extendedDurationMs ? `Resumed quota (endDate extended by ${Math.round(extendedDurationMs / 1000)}s)` : 'Resumed quota',
      payload: extendedDurationMs ? { extendedDurationMs } : undefined,
    })
  })

  return { extendedDurationMs }
}

export const isQuotaMidPeriod = (quota: Quota, nowMs: number = Date.now()): boolean => {
  if (!quota.startDate || !quota.endDate) return false
  const startMs = firestoreTimeToMillis(quota.startDate)
  const endMs = firestoreTimeToMillis(quota.endDate)
  if (endMs <= startMs) return false
  if (nowMs < startMs) return false
  return (nowMs - startMs) / (endMs - startMs) >= 0.5
}

export const updateQuotaCoachingSettings = async (
  workspaceId: string,
  settings: {
    thresholds?: number[]
    templates?: Record<QuotaCoachingTrigger, string>
    extendOnResume?: boolean
  },
  actor: { id: string; name?: string | null },
  db: Firestore = firestoreDb,
): Promise<void> => {
  const wsRef = doc(db, 'workspaces', workspaceId)
  await runTransaction(db, async (txn) => {
    const snap = await txn.get(wsRef)
    const wsData = snap.data()
    const patch: Record<string, unknown> = {}
    if (settings.thresholds !== undefined) {
      patch['settings.quotaCoachingThresholds'] = settings.thresholds
    }
    if (settings.templates !== undefined) {
      patch['settings.quotaCoachingTemplates'] = settings.templates
    }
    if (settings.extendOnResume !== undefined) {
      patch['settings.quotaExtendOnResume'] = settings.extendOnResume
    }
    txn.update(wsRef, patch)
    audit(db, txn, wsData, {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      action: 'quota.coaching.templates.updated',
      quotaId: workspaceId,
      assigneeId: actor.id,
      summary: 'Updated quota coaching templates & settings',
    })
  })
}

/** Best-effort sweep: any ACTIVE quota with endDate <= now is moved to
 *  ARCHIVED. Safe to call from a client on hub mount; idempotent. */
export const archiveExpiredQuotas = async (
  workspaceId: string,
  actorId: string,
  db: Firestore = firestoreDb,
): Promise<number> => {
  const snap = await getDocs(
    query(
      quotasCollection(db),
      where('workspaceId', '==', workspaceId),
      where('status', '==', 'ACTIVE'),
    ),
  )
  const now = Date.now()
  const expired = snap.docs.filter((d) => {
    const data = d.data() as Quota
    return data.endDate && data.endDate.toMillis() <= now
  })
  if (expired.length === 0) return 0

  const workspaceRef = doc(db, 'workspaces', workspaceId)
  await runTransaction(db, async (txn) => {
    const workspaceSnap = await txn.get(workspaceRef)
    const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined
    for (const docSnap of expired) {
      txn.update(docSnap.ref, { status: 'ARCHIVED', updatedAt: serverTimestamp(), updatedBy: actorId })
      audit(db, txn, workspace, {
        workspaceId,
        actorId,
        action: 'quota.archived',
        quotaId: docSnap.id,
        assigneeId: (docSnap.data() as Quota).assigneeId,
        summary: 'Quota auto-archived at endDate',
      })
    }
  })
  return expired.length
}

export const listActiveQuotas = async (workspaceId: string, db: Firestore = firestoreDb): Promise<Quota[]> => {
  const snap = await getDocs(
    query(
      quotasCollection(db),
      where('workspaceId', '==', workspaceId),
      where('status', '==', 'ACTIVE'),
    ),
  )
  return snap.docs
    .map((d) => toQuota(d.id, d.data()))
    .filter((quota) => !quota.deletedAt)
}

export const subscribeActiveQuotas = (
  workspaceId: string,
  assigneeId: string | null | undefined,
  callback: (items: Quota[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    callback([])
    return () => {}
  }
  const constraints = [
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'ACTIVE'),
    ...(assigneeId ? [where('assigneeId', '==', assigneeId)] : []),
  ]
  return onSnapshot(
    query(quotasCollection(db), ...constraints),
    (snapshot) => {
      callback(snapshot.docs
        .map((d) => toQuota(d.id, d.data()))
        .filter((quota) => !quota.deletedAt))
    },
    (error) => {
      console.warn('[quotaService] active quota subscription error', { workspaceId, assigneeId }, error)
      onError?.(error)
    },
  )
}

export const listHistoryQuotas = async (workspaceId: string, db: Firestore = firestoreDb): Promise<Quota[]> => {
  const snap = await getDocs(
    query(
      quotasCollection(db),
      where('workspaceId', '==', workspaceId),
      where('status', '==', 'ARCHIVED'),
    ),
  )
  return snap.docs.map((d) => toQuota(d.id, d.data()))
}

// ---------------------------------------------------------------------------
// Transactional progress — called from inside taskCompletionLock's txn so a
// quota write failure rolls the task completion back.
// ---------------------------------------------------------------------------

export interface ProgressDelta {
  tasks_completed?: number
  sales_units?: number
  checklists_done?: number
  weighted_progress?: number
  hours_logged?: number
  customTargets?: Record<string, number>
}

export const writeQuotaRewardGrant = async (
  input: {
    workspaceId: string
    quotaId: string
    memberId: string
    tier: { percent: number; label: string; badgeUrl?: string | null }
    note?: string
    actor: { id: string; name?: string | null }
  },
  db: Firestore = firestoreDb
): Promise<string> => {
  const { workspaceId, quotaId, memberId, tier, note, actor } = input
  const ledgerId = `${quotaId}__${memberId}__${tier.percent}`
  const docRef = doc(db, 'quota_rewards_ledger', ledgerId)

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(docRef)
    if (snap.exists()) return

    txn.set(docRef, {
      workspaceId,
      quotaId,
      memberId,
      tier,
      awardedAt: serverTimestamp(),
      awardedById: actor.id,
      note: note ?? null,
    })

    const workspaceRef = doc(db, 'workspaces', workspaceId)
    const workspaceSnap = await txn.get(workspaceRef)
    const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined

    audit(db, txn, workspace, {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      action: 'quota.reward.grant',
      quotaId,
      assigneeId: memberId,
      summary: `Granted reward tier ${tier.label} (${tier.percent}%)`,
      payload: { tier },
    })
  })

  return ledgerId
}

export interface QuotaRewardLedgerEntry {
  id: string
  workspaceId: string
  quotaId: string
  memberId: string
  tier: { percent: number; label: string; badgeUrl?: string | null }
  awardedAt: Timestamp | null
  awardedById: string
  note: string | null
  [key: string]: unknown
}

export const subscribeQuotaRewardLedger = (
  workspaceId: string,
  onData: (items: QuotaRewardLedgerEntry[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb
) => {
  if (!workspaceId) {
    onData([])
    return () => {}
  }
  return onSnapshot(
    query(collection(db, 'quota_rewards_ledger'), where('workspaceId', '==', workspaceId)),
    (snapshot) => {
      onData(snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as QuotaRewardLedgerEntry)))
    },
    (error) => {
      console.warn('[quotaService] subscribeQuotaRewardLedger error', { workspaceId }, error)
      onError?.(error)
    }
  )
}


export const subscribeQuotaMemberContributions = (
  quotaId: string,
  onData: (items: QuotaMemberContribution[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!quotaId) {
    onData([])
    return () => {}
  }
  return onSnapshot(
    collection(db, QUOTA_COLLECTION, quotaId, 'member_contributions'),
    (snapshot) => {
      onData(
        snapshot.docs.map((d) => ({
          id: d.id,
          quotaId,
          memberId: d.id,
          delta: d.data().delta ?? {},
          updatedAt: d.data().updatedAt ?? null,
          ...d.data(),
        }) as QuotaMemberContribution),
      )
    },
    (error) => {
      console.warn('[quotaService] subscribeQuotaMemberContributions error', { quotaId }, error)
      onError?.(error)
    },
  )
}

export const incrementProgressInTransaction = async (
  txn: Pick<Transaction, 'get' | 'set' | 'update'>,
  args: {
    db: Firestore
    workspaceId: string
    assigneeId: string
    delta: ProgressDelta
  },
): Promise<{ crossedToMet: boolean; quotaId: string } | null> => {
  const { db, workspaceId, assigneeId, delta } = args
  const hasDelta = Object.values(delta).some((value) => typeof value === 'number' && value > 0) || Object.values(delta.customTargets ?? {}).some((v) => v > 0)
  if (!hasDelta) return null

  // Part 3C: Team rollup lookup
  // If there's an active team quota whose teamMemberIds includes the committing member (assigneeId),
  // aggregate onto the team quota. Otherwise fall back to individual quota.
  const allActiveQuery = query(
    quotasCollection(db),
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'ACTIVE'),
  )
  const snap = await getDocs(allActiveQuery)
  const nonDeletedDocs = snap.docs.filter((d) => !d.data().deletedAt)

  const teamQuotaDoc = nonDeletedDocs.find((d) => {
    const data = d.data()
    return Array.isArray(data.teamMemberIds) && data.teamMemberIds.length > 0 && data.teamMemberIds.includes(assigneeId)
  })

  const isTeamQuota = Boolean(teamQuotaDoc)
  const active = teamQuotaDoc ?? nonDeletedDocs.find((d) => d.data().assigneeId === assigneeId)
  if (!active) return null

  const quota = toQuota(active.id, active.data())
  if (quota.pausedAt) return null

  const startMs = quota.startDate?.toMillis?.() ?? 0
  const eventTimeMs = Date.now()
  if (eventTimeMs < startMs) return null

  const preAttainment = quotaAttainment({ targets: quota.targets, customTargets: quota.customTargets })
  const wasMet = isQuotaMet({ targets: quota.targets, customTargets: quota.customTargets })

  const updatedTargets: QuotaTargetMap = { ...quota.targets }
  for (const key of QUOTA_TARGET_TYPES) {
    const increment = delta[key]
    if (!increment || increment <= 0) continue
    const entry = updatedTargets[key]
    if (!entry) continue
    updatedTargets[key] = { ...entry, currentValue: entry.currentValue + increment }
  }

  const updatedCustomTargets = { ...(quota.customTargets ?? {}) }
  for (const [key, increment] of Object.entries(delta.customTargets ?? {})) {
    if (!increment || increment <= 0) continue
    const entry = updatedCustomTargets[key]
    if (!entry) continue
    updatedCustomTargets[key] = { ...entry, currentValue: entry.currentValue + increment }
  }

  const postAttainment = quotaAttainment({ targets: updatedTargets, customTargets: updatedCustomTargets })
  const nowMet = isQuotaMet({ targets: updatedTargets, customTargets: updatedCustomTargets })
  const crossedToMet = !wasMet && nowMet

  const update: Record<string, unknown> = {
    targets: updatedTargets,
    customTargets: updatedCustomTargets,
    updatedAt: serverTimestamp(),
    updatedBy: assigneeId,
  }
  if (crossedToMet && !quota.celebratedAt) {
    update.celebratedAt = serverTimestamp()
  }
  txn.update(active.ref, update)

  // Track per-member contribution under quotas/{quotaId}/member_contributions/{memberId} for team quotas
  if (isTeamQuota) {
    const contribRef = doc(db, QUOTA_COLLECTION, active.id, 'member_contributions', assigneeId)
    const existingContribSnap = await txn.get(contribRef)
    const existingData = existingContribSnap.exists() ? existingContribSnap.data() : null
    const prevDelta = (existingData?.delta as ProgressDelta) ?? {}
    const updatedMemberDelta: ProgressDelta = {
      ...prevDelta,
      tasks_completed: (prevDelta.tasks_completed ?? 0) + (delta.tasks_completed ?? 0),
      sales_units: (prevDelta.sales_units ?? 0) + (delta.sales_units ?? 0),
      checklists_done: (prevDelta.checklists_done ?? 0) + (delta.checklists_done ?? 0),
      weighted_progress: (prevDelta.weighted_progress ?? 0) + (delta.weighted_progress ?? 0),
      hours_logged: (prevDelta.hours_logged ?? 0) + (delta.hours_logged ?? 0),
      customTargets: {
        ...(prevDelta.customTargets ?? {}),
      },
    }
    for (const [kpiKey, val] of Object.entries(delta.customTargets ?? {})) {
      if (val && val > 0) {
        updatedMemberDelta.customTargets![kpiKey] = ((prevDelta.customTargets ?? {})[kpiKey] ?? 0) + val
      }
    }
    txn.set(
      contribRef,
      {
        id: assigneeId,
        memberId: assigneeId,
        quotaId: active.id,
        delta: updatedMemberDelta,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    )
  }

  // Asynchronous side-effects for reward tiers
  const prePercent = Math.floor(preAttainment * 100)
  const postPercent = Math.floor(postAttainment * 100)
  
  if (quota.rewardTiers) {
    for (const tier of quota.rewardTiers) {
      if (prePercent < tier.percent && postPercent >= tier.percent) {
        writeQuotaRewardGrant({
          workspaceId,
          quotaId: quota.id,
          memberId: assigneeId,
          tier,
          actor: { id: assigneeId, name: null }
        }, db).catch(e => console.warn('[quotaService] writeQuotaRewardGrant failed', e))
        
        queueQuotaMetCelebration(db, quota).catch(e => console.warn('[quotaService] queueQuotaMetCelebration failed', e))
      }
    }
  }

  return { crossedToMet, quotaId: active.id }
}

// ---------------------------------------------------------------------------
// AI Butler coaching — fires once per period when the user is behind pace.
// ---------------------------------------------------------------------------

const ensurePerformanceChannel = async (db: Firestore, workspaceId: string): Promise<string | null> => {
  try {
    const channelsRef = collection(db, 'workspaces', workspaceId, 'channels')
    const existing = await getDocs(query(channelsRef, where('slug', '==', PERFORMANCE_CHANNEL_SLUG)))
    if (!existing.empty) return existing.docs[0].id

    const slugRef = doc(db, 'workspaces', workspaceId, 'channelSlugs', PERFORMANCE_CHANNEL_SLUG)
    const channelRef = doc(channelsRef)
    await runTransaction(db, async (txn) => {
      const slugSnap = await txn.get(slugRef)
      if (slugSnap.exists()) return
      txn.set(channelRef, {
        name: 'Performance',
        slug: PERFORMANCE_CHANNEL_SLUG,
        type: 'public',
        roles: {},
        lastActivity: serverTimestamp(),
        lastReadAt: {},
        createdBy: AI_BUTLER_UID,
        createdAt: serverTimestamp(),
      })
      txn.set(slugRef, { channelId: channelRef.id, createdBy: AI_BUTLER_UID })
    })
    return channelRef.id
  } catch (error) {
    console.warn('[quotaService] ensurePerformanceChannel failed', { workspaceId }, error)
    return null
  }
}

export const dispatchCoaching = async (
  db: Firestore,
  workspaceId: string,
  quota: Quota,
  nowMs: number = Date.now(),
): Promise<string[]> => {
  if (!quota.startDate || !quota.endDate) return []
  const startMs = firestoreTimeToMillis(quota.startDate)
  const endMs = firestoreTimeToMillis(quota.endDate)
  if (nowMs < startMs) return []

  const workspaceRef = doc(db, 'workspaces', workspaceId)
  const quotaRef = quotaDocRef(db, quota.id)
  const dispatchedTriggers: string[] = []

  try {
    await runTransaction(db, async (txn) => {
      const workspaceSnap = await txn.get(workspaceRef)
      const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined
      const quotaSnap = await txn.get(quotaRef)
      if (!quotaSnap.exists()) return
      const fresh = quotaSnap.data() as Quota

      const currentDispatched = fresh.coaching?.triggersDispatched ?? []
      const attainment = quotaAttainment({ targets: fresh.targets, customTargets: fresh.customTargets })

      const thresholds = (workspace?.settings?.quotaCoachingThresholds as number[] | undefined) ?? DEFAULT_QUOTA_COACHING_THRESHOLDS
      const templates = (workspace?.settings?.quotaCoachingTemplates as Record<QuotaCoachingTrigger, string> | undefined) ?? DEFAULT_QUOTA_COACHING_TEMPLATES

      const t60 = thresholds[0] ?? 0.6
      const t80 = thresholds[1] ?? 0.8

      const candidates: QuotaCoachingTrigger[] = []

      // 60% threshold
      if (attainment >= t60 && !currentDispatched.includes('sixty')) {
        candidates.push('sixty')
      }
      // 80% threshold
      if (attainment >= t80 && !currentDispatched.includes('eighty')) {
        candidates.push('eighty')
      }
      // met threshold (100%) - does not double-fire
      if (attainment >= 1.0 && !currentDispatched.includes('met')) {
        candidates.push('met')
      }
      // near-miss: 48 hours before endDate and attainment < 100%
      const msUntilEnd = endMs - nowMs
      const is48hWindow = msUntilEnd > 0 && msUntilEnd <= 48 * 3600 * 1000
      if (is48hWindow && attainment < 1.0 && !currentDispatched.includes('nearMiss')) {
        candidates.push('nearMiss')
      }

      if (candidates.length === 0) return

      const channelId = await ensurePerformanceChannel(db, workspaceId)
      const targetChannel = channelId ?? `dm-${quota.assigneeId}`
      const name = quota.assigneeId
      const attainmentPct = Math.round(attainment * 100)

      let lastMessageRefPath: string | null = null

      for (const trigger of candidates) {
        const rawTmpl = templates[trigger] || DEFAULT_QUOTA_COACHING_TEMPLATES[trigger]
        const text = rawTmpl
          .replace(/\{name\}/g, name)
          .replace(/\{attainment\}/g, String(attainmentPct))

        const messageRef = doc(collection(db, 'workspaces', workspaceId, 'channels', targetChannel, 'messages'))
        lastMessageRefPath = messageRef.path

        txn.set(messageRef, {
          authorId: AI_BUTLER_UID,
          authorName: AI_BUTLER_NAME,
          authorAvatarUrl: '',
          authorRole: 'ADMIN',
          textContent: text,
          timestamp: serverTimestamp(),
          mentionedUserIds: [quota.assigneeId],
          metadata: { kind: 'QUOTA_COACHING', quotaId: quota.id, trigger },
        })

        audit(db, txn, workspace, {
          workspaceId,
          actorId: AI_BUTLER_UID,
          actorName: AI_BUTLER_NAME,
          action: 'quota.coaching.dispatched',
          quotaId: quota.id,
          assigneeId: quota.assigneeId,
          summary: `AI Butler coaching nudge dispatched (${trigger})`,
          payload: { trigger, channel: channelId ?? 'DM', attainment },
        })

        dispatchedTriggers.push(trigger)
      }

      txn.update(quotaRef, {
        coaching: {
          triggeredAt: serverTimestamp(),
          channel: channelId ? 'PERFORMANCE_CHANNEL' : 'DM',
          messageRef: lastMessageRefPath,
          triggersDispatched: [...currentDispatched, ...candidates],
        },
      })
    })
  } catch (error) {
    console.warn('[quotaService] dispatchCoaching failed', { workspaceId, quotaId: quota.id }, error)
  }

  return dispatchedTriggers
}

const emitQuotaMetAudit = async (
  db: Firestore,
  workspaceId: string,
  quotaId: string,
  assigneeId: string,
  actor: AutomationActor,
): Promise<void> => {
  const workspaceRef = doc(db, 'workspaces', workspaceId)
  try {
    await runTransaction(db, async (txn) => {
      const workspaceSnap = await txn.get(workspaceRef)
      const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined
      audit(db, txn, workspace, {
        workspaceId,
        actorId: actor.id,
        actorName: actor.name ?? null,
        action: 'quota.met',
        quotaId,
        assigneeId,
        summary: 'Quota met (all targets reached)',
      })
    })
  } catch (error) {
    console.warn('[quotaService] emitQuotaMetAudit failed', { workspaceId, quotaId }, error)
  }
}

// ---------------------------------------------------------------------------
// Celebration queueing — appends a PendingCelebration to the assignee's
// gamification doc when a quota crosses to met. The ExperienceEngine
// snapshot-listens to this doc; the celebration only renders when the
// `quota.met.v1` effect is present in the registry's celebrationCatalog.
// Idempotency is guarded by the quota's celebratedAt timestamp upstream.
// ---------------------------------------------------------------------------

export const queueQuotaMetCelebration = async (db: Firestore, quota: Quota): Promise<void> => {
  if (!quota.assigneeId) return
  const ref = doc(db, 'users', quota.assigneeId, 'gamification', 'pending_celebrations')
  try {
    await runTransaction(db, async (txn) => {
      const snap = await txn.get(ref)
      const existing = snap.exists() && Array.isArray(snap.data()?.items)
        ? (snap.data()!.items as Array<{ id: string }>)
        : []
      const itemId = `quota-met-${quota.id}`
      if (existing.some((item) => item.id === itemId)) return
      const next = [
        ...existing,
        {
          id: itemId,
          action: 'TASK_COMPLETE',
          effect: 'quota.met.v1',
          messageKey: 'quota.met',
          title: 'Quota met',
          createdAt: new Date().toISOString(),
          registryVersion: 'quota-v1',
        },
      ]
      txn.set(ref, { items: next }, { merge: true })
    })
  } catch (error) {
    console.warn('[quotaService] queueQuotaMetCelebration write failed', { quotaId: quota.id }, error)
  }
}

// ---------------------------------------------------------------------------
// Orchestrator listener — registered eagerly on module import.
// ---------------------------------------------------------------------------

let listenerRegistered = false

export const registerQuotaListener = (db: Firestore = firestoreDb): void => {
  if (listenerRegistered) return
  listenerRegistered = true
  subscribeInternal('TASK_COMPLETED', async (event: AutomationTriggerEvent, actor: AutomationActor) => {
    if (event.type !== 'TASK_COMPLETED') return
    const { workspaceId, payload } = event
    if (!payload?.assigneeId) return

    // Re-evaluate threshold + 100% audit AFTER the in-transaction increment
    // has already committed. The quota doc reads here are post-write.
    const activeQuery = query(
      quotasCollection(db),
      where('workspaceId', '==', workspaceId),
      where('assigneeId', '==', payload.assigneeId),
      where('status', '==', 'ACTIVE'),
    )
    const snap = await getDocs(activeQuery)
    const active = snap.docs.find((d) => !d.data().deletedAt)
    if (!active) return
    const quota = { id: active.id, ...(active.data() as Omit<Quota, 'id'>) } as Quota

    if (isQuotaMet({ targets: quota.targets })) {
      await emitQuotaMetAudit(db, workspaceId, quota.id, quota.assigneeId, actor)
      // Celebration: queue once via the celebratedAt guard set in the
      // transactional increment. The engine consumes the assignee's
      // pending_celebrations doc; registering the `quota.met.v1` effect in
      // the celebration catalog activates the visual.
      queueQuotaMetCelebration(db, quota).catch((error) =>
        console.warn('[quotaService] queueQuotaMetCelebration failed', { quotaId: quota.id }, error),
      )
    }

    await dispatchCoaching(db, workspaceId, quota)
  })
}

registerQuotaListener()
